// The consent page's URL rules (docs/plans/claude-connector.md §3): which request a `/connect`
// URL names, how a sign-in finds its way back to it (or to a creative's files page, the link
// Claude hands a person), and where an answer may send the browser.
//
// `/oauth/authorize` sends the person to `/connect?request=<id>`. Without a session, the page
// sends them to `/login?next=connect&request=<id>`, and a sign-in there returns them to the
// request. `next` is a closed set (only `connect`), never a URL, so a crafted login link can
// send nobody anywhere but a consent page of this app.

/** A request id: 32 random bytes, base64url, as the API mints them (43 characters). */
export const CONNECT_REQUEST_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** The request id a search value names, or undefined when it is not one. */
export function parseConnectRequest(value: unknown): string | undefined {
  return typeof value === 'string' && CONNECT_REQUEST_PATTERN.test(value) ? value : undefined;
}

/** `/connect`'s search: the request it decides, when the URL names a well-formed one. */
export interface ConnectSearch {
  request?: string;
}

export function parseConnectSearch(search: Record<string, unknown>): ConnectSearch {
  const request = parseConnectRequest(search.request);
  return request ? { request } : {};
}

/**
 * A creative id as the API accepts it (`shared::ids` slug ids): what `/creatives/<id>/assets`
 * names, the page Claude sends a person to for a creative's files.
 */
export const CREATIVE_ID_PATTERN = /^[a-z0-9_-]{1,64}$/;

/** The creative id a value names, or undefined when it is not one. */
export function parseCreativeId(value: unknown): string | undefined {
  return typeof value === 'string' && CREATIVE_ID_PATTERN.test(value) ? value : undefined;
}

/**
 * Where a sign-in resumes: a consent request (`/connect`), or a creative's files page
 * (`/creatives/<id>/assets`). A closed set of pages with a shape-checked id each, never a URL,
 * so a crafted login link can send nobody anywhere else.
 */
export type LoginResume =
  | { next: 'connect'; request: string }
  | { next: 'creative-assets'; creative: string };

/** `/login`'s search: one resume's keys, or none. */
export interface LoginSearch {
  next?: LoginResume['next'];
  request?: string;
  creative?: string;
}

/** Anything but a well-formed resume (its `next` and that page's id) is ignored. */
export function parseLoginSearch(search: Record<string, unknown>): LoginSearch {
  return loginResume({
    next: search.next === 'connect' || search.next === 'creative-assets' ? search.next : undefined,
    request: parseConnectRequest(search.request),
    creative: parseCreativeId(search.creative),
  }) ?? {};
}

/** The resume a login search carries, or null for a plain sign-in. */
export function loginResume(search: LoginSearch): LoginResume | null {
  if (search.next === 'connect' && search.request) return { next: 'connect', request: search.request };
  if (search.next === 'creative-assets' && search.creative) {
    return { next: 'creative-assets', creative: search.creative };
  }
  return null;
}

/** The resume for a sign-in started from this page, or null when it is not a resumable page. */
export function resumeFromUrl(href: string): LoginResume | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.pathname === '/connect') {
    const request = parseConnectRequest(url.searchParams.get('request'));
    return request ? { next: 'connect', request } : null;
  }
  const assets = /^\/creatives\/([^/]+)\/assets\/?$/.exec(url.pathname);
  const creative = parseCreativeId(assets?.[1]);
  return creative ? { next: 'creative-assets', creative } : null;
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Where the API's answer sends the browser, or null to refuse it: the host must be the one the
 * page showed the person (`redirect_host`, a hostname without port), over https, or plain http
 * only to this computer (a desktop app's loopback redirect). The API already checked the
 * redirect when the app asked; this is the page keeping its own promise about the host it
 * displayed in large type.
 */
export function safeRedirectTarget(redirectTo: string, expectedHost: string): URL | null {
  let url: URL;
  try {
    url = new URL(redirectTo);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (host !== expectedHost.toLowerCase()) return null;
  if (url.protocol === 'https:') return url;
  if (url.protocol === 'http:' && LOOPBACK_HOSTS.has(host)) return url;
  return null;
}
